import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IdleService, IdleStates } from './core/services/idle/idle.service';
import { Subject, takeUntil } from 'rxjs';

import { AppConstants } from './core/constants/app.constants';
import { AppService } from './core/services/app/app.service';
import { AuthService } from './core/services/auth/auth.service';
import { DxcIconService } from '@dxc/ui-components/icon';
import { EmbedAuthService } from './core/services/auth/embed-auth.service';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Logger } from './core/services/logger/logger.service';
import { Router } from '@angular/router';
import { UrlConstants } from './core/constants/url.constants';
import { faArrowsRetweet } from '@fortawesome/pro-duotone-svg-icons';
import { faCancel, faTimes } from '@fortawesome/pro-solid-svg-icons';

declare global {
  interface Window {
    Lighthouse: any;
    hdlh: any;
  }
}
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  offline$ = this.appService.offline$;
  isAuthenticated = false;
  idleState = '';
  idleCountDown = AppConstants.idleGraceTimeInSeconds;
  newVersionDetected = false;
  isEmbed = false;
  constructor(
    private authService: AuthService,
    private appService: AppService,
    private logger: Logger,
    private idleService: IdleService,
    private router: Router,
    private iconService: DxcIconService,
    private embedAuthService: EmbedAuthService
  ) {
    const iconItems: IconDefinition[] = [faArrowsRetweet, faTimes];
    this.iconService.addIcon(...iconItems);
    this.isEmbed = this.embedAuthService.isEmbedded();
  }
  ngOnInit() {
    this.authService
      .onAuthChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ isAuthenticated, userData, accessToken, idToken, errorMessage }) => {
        this.isAuthenticated = isAuthenticated;

        if (isAuthenticated && !this.idleService.isRunning()) {
          this.idleService.startTimer();
        }

        if (!isAuthenticated) {
          this.idleService.destroyTimer();
        }

        this.logger.trace('onAuthChange: ', {
          isAuthenticated,
          userData,
          accessToken,
          idToken,
          errorMessage
        });
        this.authService.setSession({
          accessToken,
          idToken,
          isAuthenticated,
          userData,
          errorMessage
        });
      });

    this.embedAuthService.isAuthenticated().subscribe(() => {
      if (
        this.isEmbed &&
        this.embedAuthService.isAuthenticated() &&
        !this.idleService.isRunning()
      ) {
        this.idleService.startTimer();
      }
    }),
      // Idle events handler
      this.idleService.idleState$
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ state, countdown }) => {
          this.logger.trace('IDLE SERVICE ', { state, countdown });
          this.isAuthenticated = this.authService.isAuthenticated();
          switch (state) {
            case IdleStates.IDLE_END: {
              this.idleState = '';
              break;
            }

            case IdleStates.COUNTDOWN: {
              this.idleState = 'COUNTDOWN';
              if (Number.isInteger(countdown) && countdown) {
                this.idleCountDown = countdown;
              }
              break;
            }

            case IdleStates.TIMEOUT: {
              this.idleCountDown = 0;
              this.logger.trace('TIMEOUT_END LOGOUT');

              if (this.isAuthenticated) {
                this.idleService.destroyTimer();
                this.authService.expireSession().subscribe(() => {
                  this.router.navigate([UrlConstants.loginUrl]);
                });
              }
              if (this.isEmbed) {
                // If embedded, redirect to the embed session invalid URL
                this.idleService.destroyTimer();
                this.embedAuthService.setSessionInvalid();
                this.router.navigate([UrlConstants.embedSessionInvalidUrl]);
              }
              break;
            }
          }
        });

    this.appService.appVersion$.pipe(takeUntil(this.destroy$)).subscribe((hash) => {
      this.newVersionDetected = !!hash;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.appService.destroy();
    this.idleService.destroy();
  }

  stopSessionTimer() {
    this.idleService.stopTimer();
  }

  reloadApp() {
    this.appService.reloadApp();
  }
}
